import { useState } from 'react'
import { PlusIcon } from 'lucide-react'
import { toast } from 'sonner'
import { NamePanel } from '@/components/forms/name-panel'
import { ListToolbar } from '@/components/layout/list-toolbar'
import { matchesStatus, type StatusFilter } from '@/lib/status-filter'
import { PageBody, PageHeader } from '@/components/layout/page-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { usePanelParam } from '@/hooks/use-panel-param'
import { useCreateCustomer, useCustomers, useUpdateCustomer } from '@/hooks/use-customers'
import { useProjects } from '@/hooks/use-projects'

export function CustomersPage(): React.JSX.Element {
  const customers = useCustomers()
  const projects = useProjects()
  const createCustomer = useCreateCustomer()
  const updateCustomer = useUpdateCustomer()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<StatusFilter>('active')
  const [panel, setPanel] = usePanelParam('client')
  const editing =
    panel && panel !== 'new' ? (customers.data?.find((c) => c.id === panel) ?? null) : null
  const query = search.trim().toLowerCase()
  const visible =
    customers.data?.filter(
      (customer) =>
        matchesStatus(customer.active, status) &&
        (!query || customer.name.toLowerCase().includes(query))
    ) ?? []
  const activeCount = customers.data?.filter((customer) => customer.active).length ?? 0

  function projectCounts(customerId: string): { active: number; total: number } {
    const owned = projects.data?.filter((project) => project.customerId === customerId) ?? []
    return { active: owned.filter((project) => project.active).length, total: owned.length }
  }

  function openPanel(value: string | null): void {
    setPanel(value)
    createCustomer.reset()
    updateCustomer.reset()
  }

  function submit({ name, active }: { name: string; active: boolean }): void {
    if (!editing) {
      createCustomer.mutate(
        { name },
        {
          onSuccess: (customer) => {
            toast(`Client “${customer.name}” added`)
            openPanel(null)
          }
        }
      )
      return
    }
    updateCustomer.mutate(
      { id: editing.id, input: { name, active } },
      {
        onSuccess: (customer) => {
          if (editing.active && !customer.active) {
            toast(`Client “${customer.name}” deactivated`, {
              action: {
                label: 'Undo',
                onClick: () => updateCustomer.mutate({ id: customer.id, input: { active: true } })
              }
            })
          } else {
            toast('Client saved')
          }
          openPanel(null)
        }
      }
    )
  }

  return (
    <PageBody>
      <PageHeader
        title="Clients"
        description={
          customers.data
            ? `${activeCount} active · ${customers.data.length - activeCount} inactive · projects without a client count as internal`
            : 'Loading…'
        }
        actions={
          <Button size="sm" onClick={() => openPanel('new')}>
            <PlusIcon data-icon="inline-start" />
            New client
          </Button>
        }
      >
        <ListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search clients"
          status={status}
          onStatusChange={setStatus}
        />
      </PageHeader>

      {customers.error && <p className="text-sm text-destructive">{customers.error.message}</p>}

      <div className="overflow-x-auto rounded-lg border bg-card">
        <table className="w-full min-w-120 text-sm">
          <thead>
            <tr className="border-b bg-muted/40 text-xs text-muted-foreground">
              <th className="px-3 py-2 text-left font-medium">Name</th>
              <th className="px-3 py-2 text-right font-medium">Active projects</th>
              <th className="px-3 py-2 text-right font-medium">All projects</th>
            </tr>
          </thead>
          <tbody>
            {customers.isPending ? (
              <tr>
                <td colSpan={3} className="p-3">
                  <Skeleton className="h-20 w-full" />
                </td>
              </tr>
            ) : visible.length ? (
              visible.map((customer) => {
                const counts = projectCounts(customer.id)
                return (
                  <tr
                    key={customer.id}
                    className="cursor-pointer border-b last:border-b-0 hover:bg-muted/40"
                    onClick={() => openPanel(customer.id)}
                  >
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        className="text-left font-medium outline-none focus-visible:underline"
                        onClick={(event) => {
                          event.stopPropagation()
                          openPanel(customer.id)
                        }}
                      >
                        {customer.name}
                      </button>
                      {!customer.active && (
                        <Badge variant="secondary" className="ml-2">
                          Inactive
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{counts.active}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {counts.total}
                    </td>
                  </tr>
                )
              })
            ) : (
              <tr>
                <td colSpan={3} className="px-3 py-10 text-center text-muted-foreground">
                  {customers.data?.length
                    ? 'No clients match.'
                    : 'No clients yet. Add the first client.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <NamePanel
        open={panel === 'new' || Boolean(editing)}
        item={editing}
        noun="client"
        placeholder="e.g. Acme Labs"
        inactiveHint="Inactive clients stay on their projects but aren’t offered for new ones."
        pending={createCustomer.isPending || updateCustomer.isPending}
        error={createCustomer.error ?? updateCustomer.error}
        onOpenChange={(open) => {
          if (!open) openPanel(null)
        }}
        onSubmit={submit}
      />
    </PageBody>
  )
}
